/* bm_peakExplorer_search.js – Mapbox Search Web Component */
(function () {
  window.BM = window.BM || {};
  const BM_PEAK_EXPLORER_SEARCH_VERSION = 2;
  if (window.BM.__peakExplorerSearchVersion === BM_PEAK_EXPLORER_SEARCH_VERSION) return;
  window.BM.__peakExplorerSearchVersion = BM_PEAK_EXPLORER_SEARCH_VERSION;
  window.BM.__peakExplorerSearchLoaded = true;

  const SEARCH_READY_TIMEOUT_MS = 4000;
  const SEARCH_ERROR_MESSAGE =
    'Die Suche ist momentan nicht verfügbar. Die Position kann weiterhin direkt auf der Karte gewählt werden.';
  const SEARCH_RESULT_ERROR_MESSAGE =
    'Das Suchergebnis konnte nicht übernommen werden. Bitte einen anderen Treffer wählen.';
  const DEFAULT_SEARCH_OPTIONS = Object.freeze({
    types: 'poi,category,place,city,locality,region,street,address',
    language: 'de',
    country: 'de,at,ch,it,fr',
    limit: 8
  });

  const POI_CATEGORIES = Object.freeze([
    'peak', 'mountain', 'hill', 'mountain_range', 'pass', 'viewpoint',
    'lake', 'reservoir', 'river', 'waterfall', 'glacier',
    'national_park', 'nature_reserve', 'park', 'alpine_hut'
  ]);

  const POI_EXCLUSIONS = Object.freeze([
    'restaurant', 'cafe', 'bar', 'fast_food',
    'supermarket', 'convenience_store', 'shopping_mall',
    'clothing_store', 'pharmacy', 'bank', 'atm',
    'gas_station', 'car_rental', 'car_dealer',
    'hotel', 'motel', 'hostel'
  ]);

  function attachSearch(root, map, state, token, onPick) {
    const el = root?.querySelector?.('mapbox-search-box');
    if (!el || !map || !state || typeof token !== 'string' || !token) {
      return null;
    }

    let isDisposed = false;
    let cancelReadyWait = null;
    let retrieveHandler = null;
    let moveendHandler = null;
    let suggestHandler = null;
    let inputHandler = null;
    let clearHandler = null;
    let suggestErrorHandler = null;
    let keydownHandler = null;
    let searchInput = null;

    el.hidden = true;

    cancelReadyWait = ensureSearchComponentReady(
      () => {
        if (isDisposed) return;
        cancelReadyWait = null;

        const options = buildSearchOptions(root, state);
        if (!configureSearchElement(el, token, options)) {
          el.hidden = true;
          showSearchError(root, SEARCH_ERROR_MESSAGE);
          return;
        }

        retrieveHandler = (event) => {
          const feature = event?.detail?.features?.[0];
          const coordinates = feature?.geometry?.coordinates;
          if (!Array.isArray(coordinates) || coordinates.length < 2) {
            showSearchError(root, SEARCH_RESULT_ERROR_MESSAGE);
            return;
          }

          const lng = Number(coordinates[0]);
          const lat = Number(coordinates[1]);
          if (
            !Number.isFinite(lng)
            || lng < -180
            || lng > 180
            || !Number.isFinite(lat)
            || lat < -85.051129
            || lat > 85.051129
          ) {
            showSearchError(root, SEARCH_RESULT_ERROR_MESSAGE);
            return;
          }

          hideSearchError(root);
          onPick?.({ lng, lat }, { label: getResultLabel(feature) });
        };

        let suggestionCount = 0;
        suggestHandler = (event) => {
          const suggestions = event?.detail?.suggestions;
          suggestionCount = Array.isArray(suggestions) ? suggestions.length : 0;
          hideSearchError(root);
        };
        inputHandler = () => {
          suggestionCount = 0;
        };
        clearHandler = () => {
          suggestionCount = 0;
          hideSearchError(root);
        };
        suggestErrorHandler = () => {
          suggestionCount = 0;
          showSearchError(root, SEARCH_ERROR_MESSAGE);
        };

        searchInput = el.input;
        if (searchInput?.addEventListener) {
          keydownHandler = (event) => {
            selectFirstSuggestionOnEnter(event, searchInput, suggestionCount);
          };
          searchInput.addEventListener('keydown', keydownHandler, true);
        }

        moveendHandler = createProximitySync(el, map, state);

        hideSearchError(root);
        el.hidden = false;
        el.addEventListener('suggest', suggestHandler);
        el.addEventListener('suggesterror', suggestErrorHandler);
        el.addEventListener('input', inputHandler);
        el.addEventListener('clear', clearHandler);
        el.addEventListener('retrieve', retrieveHandler);
        map.on('moveend', moveendHandler);
      },
      () => {
        if (isDisposed) return;
        el.hidden = true;
        showSearchError(root, SEARCH_ERROR_MESSAGE);
      }
    );

    return function cleanupSearch() {
      isDisposed = true;

      cancelReadyWait?.();
      cancelReadyWait = null;

      if (retrieveHandler) {
        el.removeEventListener('retrieve', retrieveHandler);
        retrieveHandler = null;
      }

      if (suggestHandler) {
        el.removeEventListener('suggest', suggestHandler);
        suggestHandler = null;
      }

      if (suggestErrorHandler) {
        el.removeEventListener('suggesterror', suggestErrorHandler);
        suggestErrorHandler = null;
      }

      if (inputHandler) {
        el.removeEventListener('input', inputHandler);
        inputHandler = null;
      }

      if (clearHandler) {
        el.removeEventListener('clear', clearHandler);
        clearHandler = null;
      }

      if (searchInput && keydownHandler) {
        searchInput.removeEventListener('keydown', keydownHandler, true);
        keydownHandler = null;
        searchInput = null;
      }

      if (moveendHandler) {
        map.off('moveend', moveendHandler);
        moveendHandler = null;
      }
    };
  }

  function getResultLabel(feature) {
    const properties = feature?.properties || {};
    const candidates = [
      properties.name_preferred,
      properties.name,
      feature?.name
    ];
    const label = candidates.find(value => typeof value === 'string' && value.trim());
    return label ? label.trim().slice(0, 160) : '';
  }

  function showSearchError(root, message) {
    const errorElement = root?.querySelector?.('[data-role="search-error"]');
    if (!errorElement) return;
    errorElement.textContent = message || SEARCH_ERROR_MESSAGE;
    errorElement.hidden = false;
  }

  function hideSearchError(root) {
    const errorElement = root?.querySelector?.('[data-role="search-error"]');
    if (!errorElement) return;
    errorElement.hidden = true;
  }

  function selectFirstSuggestionOnEnter(event, searchInput, suggestionCount) {
    if (
      event?.key !== 'Enter'
      || event.defaultPrevented
      || event.repeat
      || event.isComposing
      || event.keyCode === 229
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.shiftKey
      || suggestionCount < 1
      || searchInput.getAttribute?.('aria-activedescendant')
    ) {
      return;
    }

    searchInput.dispatchEvent(new KeyboardEvent('keydown', {
      key: 'ArrowDown',
      code: 'ArrowDown',
      bubbles: true,
      cancelable: true,
      composed: true
    }));
  }

  function ensureSearchComponentReady(onReady, onError) {
    if (window.customElements?.get?.('mapbox-search-box')) {
      onReady();
      return null;
    }

    if (!window.customElements?.whenDefined) {
      onError?.();
      return null;
    }

    let isCancelled = false;
    let isReady = false;
    let didReportError = false;

    const reportError = () => {
      if (isCancelled || isReady || didReportError) return;
      didReportError = true;
      onError?.();
    };

    const timeoutId = window.setTimeout(() => {
      reportError();
    }, SEARCH_READY_TIMEOUT_MS);

    window.customElements.whenDefined('mapbox-search-box').then(() => {
      if (isCancelled || isReady) return;
      isReady = true;
      window.clearTimeout(timeoutId);
      onReady();
    }).catch(() => {
      if (isCancelled || isReady) return;
      window.clearTimeout(timeoutId);
      reportError();
    });

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }

  function buildSearchOptions(root, state) {
    return {
      types: DEFAULT_SEARCH_OPTIONS.types,
      language: root.dataset.language || DEFAULT_SEARCH_OPTIONS.language,
      country: root.dataset.countries || DEFAULT_SEARCH_OPTIONS.country,
      limit: DEFAULT_SEARCH_OPTIONS.limit,
      proximity: [state.center[0], state.center[1]],
      poi_category: POI_CATEGORIES.join(','),
      poi_category_exclusions: POI_EXCLUSIONS.join(',')
    };
  }

  function applyBaseProps(el, token) {
    el.accessToken = token;
    el.mapboxgl = mapboxgl;
    el.marker = false;
  }

  function applyOptions(el, options) {
    el.setAttribute('options', JSON.stringify(options));
    el.options = Object.assign({}, el.options || {}, options);
  }

  function applyComponentOptions(el) {
    el.componentOptions = {
      allowReverse: true,
      flipCoordinates: true,
      flyTo: false
    };
  }

  function configureSearchElement(el, token, options) {
    try {
      applyBaseProps(el, token);
      applyOptions(el, options);
      applyComponentOptions(el);
      return true;
    } catch {
      return false;
    }
  }

  function createProximitySync(el, map, state) {
    let lastProx = { lng: state.center[0], lat: state.center[1] };

    return function syncProximity() {
      const center = map.getCenter();
      if (!center) return;

      if (Math.abs(center.lng - lastProx.lng) < 0.05 && Math.abs(center.lat - lastProx.lat) < 0.05) {
        return;
      }

      lastProx = { lng: center.lng, lat: center.lat };

      const nextOptions = Object.assign({}, el.options || {});
      nextOptions.proximity = [center.lng, center.lat];
      applyOptions(el, nextOptions);
    };
  }

  window.BM.attachSearch = attachSearch;
})();
